import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { Falha } from "../../components/ui.js";

/**
 * O botão "Entrar com Google".
 *
 * O script do Google é carregado **sob demanda**, e só quando há Client ID
 * configurado: uma instalação que não usa login social não deve buscar script
 * de terceiro nem mostrar um botão que falharia ao ser tocado.
 *
 * Modo popup (o padrão do GIS): a pessoa não sai desta página, e por isso a
 * configuração no Google Cloud pede *origens autorizadas*, não URI de
 * redirecionamento.
 */

const SCRIPT = "https://accounts.google.com/gsi/client";

/** O que usamos do objeto que o script instala em `window`. */
interface GsiId {
  initialize(opts: {
    client_id: string;
    callback: (r: { credential?: string }) => void;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(
    alvo: HTMLElement,
    opts: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "small" | "medium" | "large";
      text?: "signin_with" | "signup_with" | "continue_with";
      shape?: "rectangular" | "pill";
      width?: number;
      locale?: string;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GsiId } };
  }
}

/** Carrega o script uma vez só, mesmo com várias montagens. */
let carregando: Promise<void> | null = null;

function carregarGsi(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (carregando) return carregando;

  carregando = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SCRIPT;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      // Deixa tentar de novo numa próxima montagem: a falha costuma ser rede
      // instável, não ausência permanente do script.
      carregando = null;
      reject(new Error("Não foi possível carregar o Google."));
    };
    document.head.appendChild(el);
  });

  return carregando;
}

export function BotaoGoogle({
  texto = "continue_with",
  divisor = "antes",
}: {
  /**
   * O rótulo, escolhido entre os quatro que o Google oferece.
   *
   * O texto é desenhado por eles, dentro de um iframe, e traduzido pelo
   * `locale` — não há como escrever o nosso. Em pt-BR as opções viram
   * "Fazer login com o Google", "Inscrever-se no Google" e "Continuar com o
   * Google". A segunda é a que descreveria o cadastro, mas "inscrever-se **no**
   * Google" soa como criar conta lá, e não aqui; `continue_with` é a única que
   * serve aos dois caminhos sem prometer a coisa errada — e é honesta, porque
   * este botão de fato entra **ou** cadastra, conforme o e-mail já exista.
   */
  texto?: "signin_with" | "signup_with" | "continue_with";
  /**
   * De que lado fica o "ou".
   *
   * No login o botão vem depois do formulário; no cadastro, antes. O divisor
   * separa dois caminhos, então precisa ficar entre eles nos dois casos.
   */
  divisor?: "antes" | "depois";
}) {
  const alvo = useRef<HTMLDivElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const publico = trpc.config.getPublic.useQuery();
  const clientId = publico.data?.googleClientId ?? "";

  const entrar = trpc.auth.entrarComGoogle.useMutation({
    onSuccess: async () => {
      setErro(null);
      await queryClient.invalidateQueries();
    },
    onError: (e) => setErro(e.message),
  });

  // `entrar` fora das dependências de propósito: a mutação do React Query muda
  // de identidade a cada render, e incluí-la reinicializaria o Google em loop.
  useEffect(() => {
    if (clientId === "" || !alvo.current) return;

    let vivo = true;

    void carregarGsi()
      .then(() => {
        const id = window.google?.accounts?.id;
        if (!vivo || !id || !alvo.current) return;

        id.initialize({
          client_id: clientId,
          callback: (r) => {
            if (!r.credential) {
              setErro("O Google não devolveu as credenciais. Tente novamente.");
              return;
            }
            entrar.mutate({ idToken: r.credential });
          },
        });

        id.renderButton(alvo.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: texto,
          shape: "rectangular",
          // Acompanha a largura do formulário no celular, onde o botão precisa
          // ser tão tocável quanto o de entrar.
          width: alvo.current.offsetWidth || 320,
          locale: "pt-BR",
        });
      })
      .catch((e: Error) => setErro(e.message));

    return () => {
      vivo = false;
    };
  }, [clientId, texto]);

  // Sem Client ID não há login social nesta instalação: nada aparece.
  if (clientId === "") return null;

  const risco = (
    <div className="flex items-center gap-3" aria-hidden>
      <span className="h-px flex-1 bg-borda" />
      <span className="text-[0.8125rem] text-texto-fraco">ou</span>
      <span className="h-px flex-1 bg-borda" />
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {divisor === "antes" && risco}

      {/* Altura reservada: sem ela, o botão do Google aparecendo empurra o
          formulário para baixo bem quando alguém vai tocá-lo. */}
      <div ref={alvo} className="flex min-h-[44px] justify-center" />

      {erro && <Falha mensagem={erro} />}

      {divisor === "depois" && risco}
    </div>
  );
}
